/**
 * CDT code treatment category constants, default ranges, and utility functions
 *
 * Single source of truth for treatment category types and the 12 ADA default
 * CDT code ranges (D0100–D9999). Client-safe — only type-only model imports
 * (erased at build time).
 *
 * @see CdtCodeMapping — custom per-workspace overrides
 */

/** 12 ADA treatment category buckets */
export const TreatmentCategory = {
  DIAGNOSTIC: `Diagnostic`,
  PREVENTATIVE: `Preventative`,
  RESTORATIVE: `Restorative`,
  ENDODONTICS: `Endodontics`,
  PERIODONTICS: `Periodontics`,
  PROSTHODONTICS_REMOVABLE: `Prosthodontics Removable`,
  MAXILLOFACIAL_PROSTHETICS: `Maxillofacial Prosthetics`,
  IMPLANT_SERVICES: `Implant Services`,
  PROSTHODONTICS_FIXED: `Prosthodontics Fixed`,
  ORAL_MAXILLOFACIAL_SURGERY: `Oral & Maxillofacial Surgery`,
  ORTHODONTICS: `Orthodontics`,
  ADJUNCTIVE_GENERAL_SERVICES: `Adjunctive General Services`,
} as const

export type TreatmentCategoryValue = (typeof TreatmentCategory)[keyof typeof TreatmentCategory]

export const treatmentCategories: TreatmentCategoryValue[] = Object.values(TreatmentCategory)

/** Default CDT code ranges mapping numeric portion to treatment category [startNum, endNum, category] */
export const defaultCdtRanges: [number, number, TreatmentCategoryValue][] = [
  [100, 999, TreatmentCategory.DIAGNOSTIC],
  [1000, 1999, TreatmentCategory.PREVENTATIVE],
  [2000, 2999, TreatmentCategory.RESTORATIVE],
  [3000, 3999, TreatmentCategory.ENDODONTICS],
  [4000, 4999, TreatmentCategory.PERIODONTICS],
  [5000, 5899, TreatmentCategory.PROSTHODONTICS_REMOVABLE],
  [5900, 5999, TreatmentCategory.MAXILLOFACIAL_PROSTHETICS],
  [6000, 6199, TreatmentCategory.IMPLANT_SERVICES],
  [6200, 6999, TreatmentCategory.PROSTHODONTICS_FIXED],
  [7000, 7999, TreatmentCategory.ORAL_MAXILLOFACIAL_SURGERY],
  [8000, 8999, TreatmentCategory.ORTHODONTICS],
  [9000, 9999, TreatmentCategory.ADJUNCTIVE_GENERAL_SERVICES],
]

const CDT_CODE_REGEX = /^[dD](\d{4})$/

/** Extracts the numeric portion from a D#### CDT code. Returns undefined for non-standard codes. */
export function parseCdtCodeNumber(code: string): number | undefined {
  const match = code.match(CDT_CODE_REGEX)
  if (!match) return undefined
  return parseInt(match[1], 10)
}

/** Returns the default treatment category for a D#### CDT code using range lookup. */
export function getDefaultCategory(code: string): TreatmentCategoryValue | undefined {
  const num = parseCdtCodeNumber(code)
  if (num === undefined) return undefined
  for (const [start, end, category] of defaultCdtRanges) {
    if (num >= start && num <= end) return category
  }
  return undefined
}

export const UNCATEGORIZED = `Uncategorized` as const

export type CodeCategory = TreatmentCategoryValue | typeof UNCATEGORIZED

export type CdtMappingLike = { cdtCode?: string | null; category?: string | null }

export type CategoryResolver = (code: string) => CodeCategory

const CDT_STEM_REGEX = /^D\d{4}/i

const NEVER_TREATMENT_STEMS = new Set([`D4910`, `D4346`])

const TREATMENT_CATEGORIES = new Set<CodeCategory>([
  TreatmentCategory.RESTORATIVE,
  TreatmentCategory.ENDODONTICS,
  TreatmentCategory.PERIODONTICS,
  TreatmentCategory.PROSTHODONTICS_REMOVABLE,
  TreatmentCategory.MAXILLOFACIAL_PROSTHETICS,
  TreatmentCategory.IMPLANT_SERVICES,
  TreatmentCategory.PROSTHODONTICS_FIXED,
  TreatmentCategory.ORAL_MAXILLOFACIAL_SURGERY,
  TreatmentCategory.ORTHODONTICS,
])

const stemOf = (code: string) => code.match(CDT_STEM_REGEX)?.[0].toUpperCase()

/**
 * Resolves a procedure code to a category: mapping on the exact code, mapping on
 * the D#### stem, ADA range of the stem, then Uncategorized.
 * Mappings whose category is not one of the 12 names are ignored.
 */
export function categoryResolver(mappings: CdtMappingLike[] = []): CategoryResolver {
  const mapped = new Map<string, TreatmentCategoryValue>()
  for (const { cdtCode, category } of mappings) {
    if (cdtCode && treatmentCategories.includes(category as TreatmentCategoryValue)) {
      mapped.set(cdtCode.toUpperCase(), category as TreatmentCategoryValue)
    }
  }
  return (code) => {
    const upper = code.trim().toUpperCase()
    const stem = stemOf(upper)
    const exact = mapped.get(upper)
    if (exact) return exact
    if (!stem) return UNCATEGORIZED
    return mapped.get(stem) ?? getDefaultCategory(stem) ?? UNCATEGORIZED
  }
}

export function isTreatmentCode(code: string, categoryOf: CategoryResolver): boolean {
  const stem = stemOf(code)
  if (stem && NEVER_TREATMENT_STEMS.has(stem)) return false
  return TREATMENT_CATEGORIES.has(categoryOf(code))
}

/**
 * Resolves multiple procedure codes to deduplicated categories.
 * Pure function: no DB access, suitable for client-side use.
 */
export function getTreatmentCategories(codes: string[], customMappings: CdtMappingLike[] = []): CodeCategory[] {
  return Array.from(new Set(codes.map(categoryResolver(customMappings))))
}
